import { useCallback, useState } from 'react'
import {
  uploadDocument,
  useCreateDocumentMutation,
  useLinkStartupDocumentMutation,
  useStartupDocumentsQuery,
  useUnlinkStartupDocumentMutation,
  useVerifyDocumentMutation,
} from './api'

export const DECK_MAX_BYTES = 50 * 1024 * 1024

/** The rejection messages, phrased for a founder rather than a validator. */
export function deckRejectionMessage(code: string | undefined, fallback?: string): string {
  if (code === 'file-too-large') return 'File is too large. Maximum size is 50 MB.'
  if (code === 'file-invalid-type') return 'Only PDF files are accepted.'
  return fallback ?? 'File was rejected.'
}

/**
 * Replace the startup's pitch deck.
 *
 * Extracted from the feedback page's improve-score card so Home's upload state
 * can offer the same thing: two entry points to "give us your deck" that
 * uploaded it differently would be two ways to get a different result.
 *
 * The order matters and is the reason this is a hook rather than a mutation
 * call — the new deck is uploaded and verified *before* the old one is taken
 * off the profile, so a failure anywhere leaves the founder's existing deck
 * intact. The old deck is only unlinked: the server keeps it as an earlier,
 * dated version, so it is never deleted here. Replacing a deck does not
 * re-evaluate it; that is the founder's explicit "Run feedback".
 */
export function useDeckUpload(startupId: string) {
  const [isUploading, setIsUploading] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: documents } = useStartupDocumentsQuery(startupId)
  const createDoc = useCreateDocumentMutation()
  const verifyDoc = useVerifyDocumentMutation()
  const linkDoc = useLinkStartupDocumentMutation(startupId)
  const unlinkDoc = useUnlinkStartupDocumentMutation(startupId)

  const upload = useCallback(
    async (file: File): Promise<boolean> => {
      setIsUploading(true)
      setError(null)
      setIsDone(false)
      try {
        const previousDeck = documents?.find((doc) => doc.type === 'PITCH_DECK')

        // Upload + verify the new deck first, so a failure leaves the old intact.
        const { documentId } = await uploadDocument(file, createDoc, verifyDoc)

        await linkDoc.mutateAsync({
          path: { startupId },
          body: { documentId, type: 'PITCH_DECK' },
        })

        // Take the previous deck off the profile once the new one is in place.
        if (previousDeck && previousDeck.documentId !== documentId) {
          await unlinkDoc.mutateAsync({
            path: { startupId, documentId: previousDeck.documentId },
          })
        }

        setIsDone(true)
        return true
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed')
        return false
      } finally {
        setIsUploading(false)
      }
    },
    [startupId, documents, createDoc, verifyDoc, linkDoc, unlinkDoc]
  )

  return { upload, isUploading, isDone, error, setError }
}
